import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import useIsScrolled from 'hooks/useIsScrolled';
import useScrollDirection from 'hooks/useScrollDirection';
import useGlobalNavigation from 'hooks/useGlobalNavigation';
import useBreakpointView from 'utils/useBreakpointView';
import AnnouncementBarContext from 'utils/AnnouncementBarContext';
import { isProd } from 'utils/urlHelper';
import useDomElement from 'hooks/useDomElement';
import DesktopMenu from './DesktopMenu';
import Mobile from './Mobile';
import AnnouncementBar from './Banner/AnnouncementBar';
import PromoBar from './Banner/PromoBar';
import { WebsiteHeaderContainer, WebsiteHeaderInnerContainer } from './styles';

const WebsiteHeader = ({
  announcementBar,
  globalPromoBar,
  promoBar,
  background,
  contextualCta,
  customPhoneNumber,
  landingPage,
  showAdditionalMenuItems,
  logoRoute,
  logoType,
  navigation,
  showMainMenuItems,
  noHero,
  theme,
  mobileUseGlobalNav,
  mobileTransparentNav,
  SubNav,
  inline,
}) => {
  const isMobile = useBreakpointView(['xs', 'sm', 'md', 'lg']);
  const [isMobileMenuOpened, setMobileMenuOpened] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [announcementBarHeight, setAnnouncementBarHeight] = useState(0);
  const [shouldShowGlobalPromoBar, setShouldShowGlobalPromoBar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { isScrolled: announcementBarScroll } = useIsScrolled(30);
  const { isScrolled: shrinkNavScroll } = useIsScrolled(300);
  const scrollDirection = useScrollDirection({ thresholdPixels: 5 });
  const showMenuItems = showMainMenuItems || !landingPage;
  const globalNavigation = useGlobalNavigation()?.navigation;
  const isGlobal =
    (useBreakpointView(['xs', 'sm', 'md', 'lg']) && mobileUseGlobalNav) || !navigation?.navigation;
  const navigationMenu = [...(isGlobal ? globalNavigation : navigation?.navigation)];
  const backgroundColor = theme === 'SIMPLE_HEADER' || noHero ? 'var(--white)' : background;
  const { hideAnnouncementBar } = useContext(AnnouncementBarContext);
  const hasPromoBar = promoBar?.desktopPromoCopy;
  const announcementBarEl = useDomElement('.website-header--announcement-bar');
  const path = typeof window !== 'undefined' ? window.location.pathname : '';

  const hasGlobalPromoBar = () => {
    if (isProd && globalPromoBar?.hide) {
      return false;
    }

    if (path && globalPromoBar?.excludePaths?.excludePaths) {
      const excludePaths = globalPromoBar.excludePaths.excludePaths
        .split('\n')
        .map((item) => item.trim());
      return !excludePaths.some((excludePath) => {
        if (excludePath.endsWith('/*')) {
          const prefix = excludePath.slice(0, -2);
          return path.startsWith(prefix);
        }
        return path === excludePath;
      });
    }
    return true;
  };

  useEffect(() => {
    if (globalPromoBar && !hasPromoBar) {
      const result = hasGlobalPromoBar();
      setShouldShowGlobalPromoBar(result);
      setIsLoading(false);
    }
  }, [path, globalPromoBar]);

  useEffect(() => {
    if (announcementBarScroll) setHasScrolled(true);
  }, [announcementBarScroll]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpened ? 'hidden' : 'auto';
  }, [isMobileMenuOpened]);

  useEffect(() => {
    if (!isMobile) document.body.style.overflow = 'auto';
  }, [isMobile]);

  useEffect(() => {
    if (announcementBarEl) {
      setAnnouncementBarHeight(announcementBarEl.offsetHeight + 1);
    }
  }, [announcementBarEl?.offsetHeight]);

  const headerScrollClassName =
    theme === 'SIMPLE_HEADER' || inline
      ? ''
      : `${announcementBarScroll ? 'scrolled' : ''} ` +
        `scroll-${scrollDirection} ` +
        `${shrinkNavScroll ? 'shrink-nav' : ''} ` +
        `${announcementBarScroll && scrollDirection !== 'up' ? 'hide-announcement-bar' : ''} ` +
        `${hasScrolled || noHero ? 'top' : ''}`;

  return (
    <WebsiteHeaderContainer
      id="header"
      $hideAnnouncementBar={hideAnnouncementBar}
      announcementBarHeight={announcementBarHeight}
      theme={theme}
      mobileMenuOpen={isMobileMenuOpened}
      className={`website-header ${headerScrollClassName} ${
        isMobileMenuOpened ? ' mobile-menu-opened' : ''
      } ${path === '/' || path === '/homepage-new' || path === '/success' ? 'home' : ''} ${
        SubNav ? 'subnav' : ''
      } ${mobileTransparentNav ? 'mobile-transparent' : ''}`}
      background={backgroundColor}
      data-testid="website-header"
      mobileTransparentNav={mobileTransparentNav}
    >
      <div className="website-header--main-menu">
        {hasPromoBar && <PromoBar theme={false} {...promoBar} />}
        <div>
          {!isLoading && shouldShowGlobalPromoBar && (
            <PromoBar
              theme={false}
              desktopPromoCopy={globalPromoBar?.desktopCopy}
              mobilePromoCopy={globalPromoBar?.mobileCopy}
              promoDetails={globalPromoBar?.details}
            />
          )}
        </div>
        {!hideAnnouncementBar && <AnnouncementBar theme={theme} {...announcementBar} />}
        <WebsiteHeaderInnerContainer className="website-header--inner-container">
          <Mobile
            navigationMenu={navigationMenu}
            logoProps={{ route: logoRoute, type: logoType }}
            rightMenuProps={{
              contextualCta,
              customPhoneNumber,
              isMobileMenuOpened,
              showAdditionalMenuItems,
              setMobileMenuOpened,
              showMenuItems,
              theme,
            }}
          />
          <DesktopMenu
            navigationMenu={navigationMenu}
            logoProps={{ route: logoRoute, type: logoType }}
            rightMenuProps={{
              contextualCta,
              showAdditionalMenuItems,
              showMenuItems,
              theme,
            }}
            showMainMenuItems={showMainMenuItems}
          />
        </WebsiteHeaderInnerContainer>
      </div>
      {SubNav && <SubNav />}
    </WebsiteHeaderContainer>
  );
};

WebsiteHeader.defaultProps = {
  showMainMenuItems: true,
};

WebsiteHeader.propTypes = {
  background: PropTypes.string,
  contextualCta: PropTypes.object,
  showAdditionalMenuItems: PropTypes.array,
  logoRoute: PropTypes.string,
  navigation: PropTypes.object,
  showMainMenuItems: PropTypes.bool,
  simpleHeader: PropTypes.bool,
};

export default WebsiteHeader;
